import React, { useState, useRef } from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Typography from "@mui/material/Typography"
import {
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material"
import IconButton from "@mui/material/IconButton"
import CloseIcon from "@mui/icons-material/Close"
import { CERTIFY_ACTION } from "../utils/certificationDecision"

const ModalCertAbove100 = ({
  open,
  onClose,
  onCertifyWithValue,
  onCertifyAt100,
  percentage,
  isCertifying,
  certifyingAction,
  partidaName,
  partidaRef,
}) => {
  const [rememberForOneHour, setRememberForOneHour] = useState(false)
  const lastPercentageRef = useRef(null)
  if (percentage != null) {
    lastPercentageRef.current = percentage
  }
  const displayPercentage = percentage ?? lastPercentageRef.current

  const handleCertifyWithValue = () => {
    onCertifyWithValue(rememberForOneHour)
  }

  const handleCertifyAt100 = () => {
    onCertifyAt100(rememberForOneHour)
  }

  return (
    <Dialog
      open={open}
      onClose={!isCertifying && onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {i18n.t("certAbove100.title")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={isCertifying}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {partidaRef && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {partidaRef}
            {partidaName && ` - ${partidaName}`}
          </Typography>
        )}
        <Typography variant="body1">
          {i18n.t("certAbove100.description")}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={rememberForOneHour}
              onChange={e => setRememberForOneHour(e.target.checked)}
              disabled={isCertifying}
            />
          }
          label={i18n.t("certAbove100.rememberForOneHour")}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          color="secondary"
          variant="text"
          disabled={isCertifying}
        >
          {i18n.t("buttons.cancel")}
        </Button>
        <Button
          onClick={handleCertifyAt100}
          color="branded"
          variant="outlined"
          disabled={isCertifying}
          sx={{ width: 190, height: 36 }}
        >
          {isCertifying && certifyingAction === CERTIFY_ACTION.AT_100 ? (
            <CircularProgress size={20} />
          ) : (
            i18n.t("certAbove100.adjustTo100")
          )}
        </Button>
        <Button
          onClick={handleCertifyWithValue}
          color="branded"
          variant="contained"
          disabled={isCertifying}
          sx={{ width: 200, minHeight: 36 }}
        >
          {isCertifying && certifyingAction === CERTIFY_ACTION.WITH_VALUE ? (
            <CircularProgress size={20} />
          ) : (
            i18n.t("certAbove100.certifyWithValue", {
              percentage: (displayPercentage ?? 0).toFixed(2),
            })
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

ModalCertAbove100.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCertifyWithValue: PropTypes.func.isRequired,
  onCertifyAt100: PropTypes.func.isRequired,
  percentage: PropTypes.number,
  isCertifying: PropTypes.bool,
  certifyingAction: PropTypes.string,
  partidaName: PropTypes.string,
  partidaRef: PropTypes.string,
}

export default ModalCertAbove100
